import { useQuery } from "@tanstack/react-query";
import { useSession, sessionScope } from "./auth";
import { apiGet, apiPost, apiPatch, apiDelete, apiPut } from "./api";
import { queryClient } from "./queryClient";
import type { ServerFile } from "./files";
export interface LibraryFolder {
  id: string;
  name: string;
  parent_id: string | null;
  room_id: string | null;
  created_by: string;
}
export interface LibraryState {
  folders: LibraryFolder[];
  starred_file_ids: string[];
}
export interface TrashFile extends ServerFile {
  deleted_at: number;
  purge_started_at?: number | null;
}
export function useFileLibrary() {
  const session = useSession();
  return useQuery({
    queryKey: ["file-library", sessionScope(session)],
    enabled: !!session,
    queryFn: () => apiGet<LibraryState>("/v1/file-library"),
  });
}
export async function refreshFileLibrary() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["file-library"] }),
    queryClient.invalidateQueries({ queryKey: ["file-trash"] }),
    queryClient.invalidateQueries({ queryKey: ["files"] }),
  ]);
}
export async function createFolder(
  name: string,
  parentId?: string,
  roomId?: string,
) {
  const result = await apiPost<{ folder: LibraryFolder }>(
    "/v1/file-library/folders",
    { name, parent_id: parentId, room_id: roomId },
  );
  await refreshFileLibrary();
  return result.folder;
}
export async function renameFolder(id: string, name: string) {
  await apiPatch(`/v1/file-library/folders/${encodeURIComponent(id)}`, {
    name,
  });
  await refreshFileLibrary();
}
export async function removeFolder(id: string) {
  await apiDelete(`/v1/file-library/folders/${encodeURIComponent(id)}`);
  await refreshFileLibrary();
}
export async function organizeFile(
  id: string,
  patch: { name?: string; folder_id: string | null },
) {
  await apiPatch(`/v1/file-library/files/${encodeURIComponent(id)}`, patch);
  await refreshFileLibrary();
}
export async function starFile(id: string, starred: boolean) {
  await apiPut(`/v1/file-library/files/${encodeURIComponent(id)}/star`, {
    starred,
  });
  await refreshFileLibrary();
}
export async function restoreFile(id: string) {
  await apiPost(`/v1/file-library/files/${encodeURIComponent(id)}/restore`, {});
  await refreshFileLibrary();
}
export async function purgeFile(id: string) {
  await apiDelete(`/v1/file-library/files/${encodeURIComponent(id)}/permanent`);
  await refreshFileLibrary();
}
export function useFileTrash() {
  const session = useSession();
  return useQuery({
    queryKey: ["file-trash", sessionScope(session)],
    enabled: !!session,
    queryFn: async () => {
      const files: TrashFile[] = [];
      let cursor: string | null = null;
      do {
        const result: { files: TrashFile[]; next_cursor: string | null } =
          await apiGet(
            `/v1/file-library/trash${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
          );
        files.push(...result.files);
        if (cursor && cursor === result.next_cursor)
          throw new Error("ごみ箱のページを読み込めませんでした");
        cursor = result.next_cursor;
      } while (cursor);
      return files;
    },
  });
}
